<script lang="ts" setup>
import { computed, inject, ref } from 'vue';

import type BigNumber from '@exchange/helpers/bignumber';
import { accountService } from '@exchange/libs/account/service/src';
import { subaccountsService, SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

import AccountTag from './components/AccountTag.vue';

defineProps({
  disabled: { type: Boolean, default: false },
});

type SubaccountsItem = {
  value: string;
  name: string;
  totalBalance: BigNumber;
  id: number;
  isMain: boolean;
  type: SubaccountType;
};

const { accountId, isSubaccountMain } = accountService;
const { defaultFiatCurrency, currencies } = currencyService;

const defaultPrecision = CONSTANTS.PRECISION.DEFAULT_FIAT;

const getAccountTypeLabel = (type: SubaccountType): string => {
  switch (type) {
    case SubaccountType.STANDARD:
      return 'Spot';
    case SubaccountType.INSTANT_TRADE:
      return 'Swap';
    case SubaccountType.SPOT:
      return 'Spot';
    case SubaccountType.FUTURES:
      return 'Futures';
    default:
      return type;
  }
};

const subaccountsList = computed<Array<SubaccountsItem>>(() =>
  subaccountsService.list.value.map((account, index) => ({
    value: account.accountId,
    name: account.name,
    totalBalance: account.getBalanceTotal(currencies.value).all,
    id: index,
    isMain: isSubaccountMain(account.accountId),
    type: account.type,
  })),
);

const balanceMasked = inject('balanceMasked', ref(false));

const chosenSubaccount = computed({
  get: () => subaccountsList.value.find((item) => item.value === accountId.value),
  set: (newValue) => {
    if (newValue) {
      subaccountsService.switchTo(newValue.value);
    }
  },
});
</script>

<template>
  <ot-select
    v-model:value="chosenSubaccount"
    class="account-select-dropdown"
    label="name"
    variant="dark-outlined"
    open-indicator-icon-name="down"
    :disabled="disabled"
    :options="subaccountsList"
  >
    <template #selected-option="subaccount">
      <div class="account-select-dropdown__selected-option">
        <div class="account-select-dropdown__selected-option-content">
          <div class="account-select-dropdown__selected-option-category">
            {{ subaccount.name }}
          </div>
          <div class="account-select-dropdown__selected-option-name">
            <ot-balance
              :value="subaccount.totalBalance"
              :precision="defaultPrecision"
              :symbol="defaultFiatCurrency"
              :masked="balanceMasked"
            />
          </div>
        </div>
        <ot-lozenge
          :text="getAccountTypeLabel(subaccount.type)"
          class="account-select-dropdown__type-lozenge account-select-dropdown__type-lozenge--selected"
          :filled="false"
        />
      </div>
    </template>
    <template #option="subaccount">
      <account-tag
        v-if="subaccount.isMain"
        class="account-select-dropdown__bullet"
        :is-main="true"
      />
      <account-tag
        v-else-if="subaccount.id === 1"
        class="account-select-dropdown__bullet"
        :is-main="false"
      />
      <x-list-item
        class="account-select-dropdown__option"
        :title="subaccount.name"
        :active="chosenSubaccount && chosenSubaccount.id === subaccount.id"
      >
        <template #subtitle>
          <ot-balance
            :value="subaccount.totalBalance"
            :precision="defaultPrecision"
            :symbol="defaultFiatCurrency"
            :masked="balanceMasked"
          />
        </template>
        <template #label-additional-content>
          <ot-lozenge
            :text="getAccountTypeLabel(subaccount.type)"
            class="account-select-dropdown__type-lozenge"
            :filled="false"
          />
        </template>
      </x-list-item>
    </template>
  </ot-select>
</template>

<style lang="scss">
.account-select-dropdown {
  --account-switch-primary-accent-color: rgb(var(--v-theme-primary));
  --account-switch-secondary-accent-color: rgb(var(--v-theme-secondary));
  --vs-border-style: none;

  height: 100%;
  border-radius: 16px;
  background-color: rgb(var(--v-theme-elevation-1));

  &__selected-option-name {
    display: flex;
    align-items: center;
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-sm);
  }

  &__bullet {
    margin: 8px 0 6px;
    pointer-events: none;
  }

  .ot-balance__number {
    font-size: var(--font-size-lg);
  }

  &__type-lozenge {
    margin: 0 8px 0 auto;
    font-size: var(--font-size-xs);

    &--selected {
      margin-left: 8px;
    }
  }

  &__selected-option-category {
    display: flex;
    align-items: center;
  }

  &__selected-option {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }

  &__selected-option-content {
    display: flex;
    flex-direction: column;
  }
}
</style>
